import React from "react";
import { FileText, Eye } from "lucide-react";
import { DropzoneRootProps, DropzoneInputProps } from "react-dropzone";

interface Attachment {
  url: string;
  filename?: string;
  name?: string;
  originalName?: string;
  uploadedAt?: string;
}

interface FileUploadProps {
  mode?: "create" | "view" | "update" | "complete" | "edit";
  files: File[] | null;
  onFilesChange: (files: File[] | null) => void;
  existingAttachments: Attachment[];
  onRemoveExistingAttachment: (index: number) => void;
  onPreviewFile: (attachment: Attachment) => void;
  uploading?: boolean;
  uploadProgress?: number;
  disabled?: boolean;
  maxFileSizeMB?: number; // Optional, defaults to 10MB
  getRootProps: <T extends DropzoneRootProps>(props?: T) => T;
  getInputProps: <T extends DropzoneInputProps>(props?: T) => T;
}

const FileUpload: React.FC<FileUploadProps> = ({
  mode = "create",
  files,
  onFilesChange,
  existingAttachments,
  onRemoveExistingAttachment,
  onPreviewFile,
  uploading = false,
  uploadProgress = 0,
  disabled = false,
  maxFileSizeMB = 10,
  getRootProps,
  getInputProps,
}) => {
  // Helper function to get file extension
  const getFileExtension = (filename: string): string => {
    return filename.split(".").pop()?.toLowerCase() || "";
  };

  // Helper function to check if file can be previewed in browser
  const canPreviewFile = (filename: string): boolean => {
    const ext = getFileExtension(filename);
    return [
      "jpg",
      "jpeg",
      "png",
      "pdf",
      "txt",
      "doc",
      "docx",
      "ppt",
      "pptx",
    ].includes(ext);
  };

  const handleRemoveFile = (index: number) => {
    if (!files) return;
    const updatedFiles = [...files];
    updatedFiles.splice(index, 1);
    onFilesChange(updatedFiles.length > 0 ? updatedFiles : null);
  };
  return (
    <div className="flex flex-col mt-6 mb-6">
      <div className="flex justify-between items-center mb-3">
        <label className="text-sm font-semibold text-gray-700">
          {mode === "view" ? "Attached Files" : "Upload Files"}
        </label>
        {mode !== "view" && (
          <p className="text-xs text-gray-500">
            Supported:{" "}
            <span className="text-[#00A8A5] font-medium">
              DOC, DOCX, PDF, XLS, XLSX, PPT, PPTX, Images
            </span>
            {maxFileSizeMB && (
              <span className="text-gray-400"> • Max: {maxFileSizeMB}MB</span>
            )}
          </p>
        )}
      </div>
      <div
        className={`border-2 border-[#a5b7cf] p-6 rounded-lg transition-all ${
          mode === "view" 
            ? "bg-gray-50 border-solid" 
            : "border-dashed hover:border-[#00A8A5] hover:bg-blue-50/30 cursor-pointer"
        } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
        {...(mode !== "view" && !disabled ? getRootProps() : {})}
      >
        {mode !== "view" && !disabled && <input {...getInputProps()} />}
        {mode === "view" ? (
          <p className="text-gray-500 text-sm font-medium mb-4">Attached Files</p>
        ) : (
          <div className="text-center mb-4">
            <svg
              className="w-12 h-12 mx-auto mb-2 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
              />
            </svg>
            <p className="text-gray-600 font-medium mb-1">
              Drag & Drop Your Files Here
            </p>
            <p className="text-gray-500 text-sm">or click to browse</p>
          </div>
        )}
        {/* Uploaded Files Preview */}
        <div className="flex gap-3 mt-4 flex-wrap">
          {/* Existing Attachments */}
          {existingAttachments.map((attachment, index) => {
            const filename =
              attachment.name ||
              attachment.originalName ||
              attachment.url?.split("/").pop() ||
              `Attachment ${index + 1}`;
            const fileExt = getFileExtension(filename).toUpperCase();

            return (
              <div
                key={`existing-${index}`}
                className="flex flex-col items-center bg-white border-2 border-gray-200 hover:border-[#00A8A5] p-4 rounded-lg min-w-[120px] max-w-[140px] relative group transition-all shadow-sm hover:shadow-md"
              >
                <div className="relative mb-2">
                  <FileText size={40} className="text-[#00A8A5]" />
                  {canPreviewFile(filename) && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onPreviewFile(attachment);
                      }}
                      className="absolute -top-1 -left-1 bg-[#00A8A5] text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-md"
                      title="Preview file"
                    >
                      <Eye size={14} />
                    </button>
                  )}
                </div>
                <p className="text-xs text-gray-700 font-medium truncate w-full text-center mb-1" title={filename}>
                  {filename.length > 20 ? `${filename.substring(0, 20)}...` : filename}
                </p>
                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                  {fileExt || "FILE"}
                </span>
                {mode !== "view" && mode === "update" && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemoveExistingAttachment(index);
                    }}
                    className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center shadow-md transition-colors"
                    disabled={disabled}
                    title="Remove file"
                  >
                    <span className="text-sm font-bold">×</span>
                  </button>
                )}
              </div>
            );
          })}

          {/* New Files */}
          {files &&
            files.map((file, index) => {
              const fileExt = getFileExtension(file.name).toUpperCase();
              return (
                <div
                  key={`new-${index}`}
                  className="flex flex-col items-center bg-white border-2 border-blue-200 hover:border-[#00A8A5] p-4 rounded-lg min-w-[120px] max-w-[140px] relative group transition-all shadow-sm hover:shadow-md"
                >
                  <div className="relative mb-2">
                    <FileText size={40} className="text-blue-500" />
                    {canPreviewFile(file.name) && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          const fileUrl = URL.createObjectURL(file);
                          window.open(fileUrl, "_blank");
                          setTimeout(() => URL.revokeObjectURL(fileUrl), 100);
                        }}
                        className="absolute -top-1 -left-1 bg-[#00A8A5] text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-md"
                        title="Preview file"
                      >
                        <Eye size={14} />
                      </button>
                    )}
                  </div>
                  <p className="text-xs text-gray-700 font-medium truncate w-full text-center mb-1" title={file.name}>
                    {file.name.length > 20 ? `${file.name.substring(0, 20)}...` : file.name}
                  </p>
                  <span className="text-xs text-gray-500 bg-blue-50 px-2 py-0.5 rounded">
                    {fileExt || "FILE"}
                  </span>
                  {mode !== "view" && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveFile(index);
                      }}
                      className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center shadow-md transition-colors"
                      disabled={disabled}
                      title="Remove file"
                    >
                      <span className="text-sm font-bold">×</span>
                    </button>
                  )}
                </div>
              );
            })}

          {/* No Files Message */}
          {(!Array.isArray(existingAttachments) ||
            existingAttachments.length === 0) &&
          (!files || files.length === 0) ? (
            <div className="w-full text-center py-5">
              <svg
                className="w-16 h-16 mx-auto mb-3 text-gray-300"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <p className="text-gray-400 text-sm font-medium">
                {mode === "view" ? "No files attached" : "No files selected"}
              </p>
            </div>
          ) : null}
        </div>
      </div>
      {uploading && mode !== "view" && (
        <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-blue-700">
              Uploading files...
            </p>
            <p className="text-sm font-semibold text-blue-600">
              {uploadProgress}%
            </p>
          </div>
          <div className="w-full bg-blue-100 rounded-full h-2.5 overflow-hidden">
            <div
              className="bg-[#00A8A5] h-2.5 rounded-full transition-all duration-300 ease-out shadow-sm"
              style={{ width: `${uploadProgress}%` }}
            ></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUpload;
